import React, { useState } from 'react';

import { Select } from '@aline/common';
import { link } from '@aline/routes';
import { useReactiveVar } from '@apollo/client/react';
import { Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  AlineButton,
  setForwardDocumentDialogState,
  setShareDocumentViaEmailDialogState,
} from 'src/components';
import { SmallDownArrowIcon } from 'src/components/icons/SmallDownArrowIcon';
import { activeAppBarVar } from 'src/editor/toolbars/editor-app-bars/EditorAppBarContent';
import { Apollo } from 'src/gql/generated';
import { useCheckWorkflowAuthorization, useStartS4SFlow } from 'src/hooks';
import { useEditorLoading } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

import type { AppBarDocument } from '../EditorAppBar';

const useStyles = makeStyles()(theme => ({
  downArrow: {
    height: 12,
    marginLeft: theme.spacing(1),
    width: 12,
  },
  menu: {
    minWidth: 174,
  },
}));

interface SendDocumentOutlinedButtonProps {
  document: AppBarDocument;
}

// TODO: If changing, consider migrating to DeclarativeMenu
export function SendDocumentOutlinedButton({
  document,
}: SendDocumentOutlinedButtonProps): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const editorLoading = useEditorLoading();
  const { startS4SFlow } = useStartS4SFlow();
  const activeAppBar = useReactiveVar(activeAppBarVar);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isAuthorizedToS4SWithoutWF = document.requesterRole === Apollo.PermissionRole.Editor;
  const { canViewWorkflow } = useCheckWorkflowAuthorization(document?.workflow?.id ?? null);
  // For cases where WF exists, it's alright to redirect WF viewer to WF builder for S4S even though only WF editors can send.
  const isAuthorizedToS4S = isAuthorizedToS4SWithoutWF || canViewWorkflow;

  return (
    <>
      <AlineButton
        ref={setAnchorEl}
        color="primary"
        data-cy={Select.document.sendDocument.button}
        disabled={editorLoading}
        loading={isSubmitting}
        variant="outlined"
        onClick={() => setMenuOpen(!menuOpen)}
      >
        Send
        <SmallDownArrowIcon className={classes.downArrow} />
      </AlineButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        classes={{ paper: classes.menu }}
        open={menuOpen}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        variant="menu"
        onClose={() => setMenuOpen(false)}
      >
        {activeAppBar === 'standard' && (
          <MenuItem
            data-cy={Select.document.sendDocument.sendForSignMenuItem}
            disabled={!isAuthorizedToS4S}
            onClick={async () => {
              setMenuOpen(false);
              if (!document.workflow) {
                setIsSubmitting(true);
                await startS4SFlow({ documentIds: [document.id] });
                setIsSubmitting(false);
                return;
              }

              navigate(link.WorkflowDetailTabSummary({ workflowId: document.workflow.id }));
            }}
          >
            Send for signature
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setMenuOpen(false);
            if (document.requesterRole === Apollo.PermissionRole.Editor) {
              setShareDocumentViaEmailDialogState({ open: true });
            } else {
              setForwardDocumentDialogState({ open: true });
            }
          }}
        >
          Send document via email
        </MenuItem>
      </Menu>
    </>
  );
}
